import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { handleError, handleSuccess } from '../../utils';
import { ToastContainer } from 'react-toastify';
import {
    Container,
    Typography,
    TextField,
    Button,
    Box,
    Stack,
    CircularProgress,
    Alert,
    LinearProgress,
    Chip,
    Autocomplete,
    Paper
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    CloudUpload as UploadIcon,
    PlayCircleOutline as PlayIcon,
    Delete as DeleteIcon,
    Language as LanguageIcon,
    Assignment as TemplateIcon,
    Edit as EditIcon
} from '@mui/icons-material';

const ProgramCreate = () => {
    const [formData, setFormData] = useState({
        language: '',
        programDetail: '',
        name: '',
        url: '',
        language_temp: '',
        tempName: '',
        ageRange: [],
        templateId: ''
    });
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Language states
    const [languages, setLanguages] = useState([]);
    const [languagesLoading, setLanguagesLoading] = useState(true);

    // Program template states
    const [programTemplates, setProgramTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editingProgramName, setEditingProgramName] = useState(false);
    const [editedProgramName, setEditedProgramName] = useState('');

    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState('');

    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

    const axiosConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
    };

    // Common TextField styling
    const textFieldStyles = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            '& fieldset': {
                borderRadius: 3,
            },
            '&:hover fieldset': {
                borderColor: '#673AB7',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#673AB7',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#673AB7',
        },
    };

    // Fetch languages from API
    const fetchLanguages = async () => {
        try {
            setLanguagesLoading(true);
            const response = await axios.get(`${API_URL}/api/languages?limit=100`, axiosConfig);
            const result = response.data;
            if (result.success) {
                setLanguages(result.data);
            } else {
                handleError('Failed to fetch languages');
                setLanguages([]);
            }
        } catch (error) {
            console.error('Error fetching languages:', error);
            handleError('Failed to fetch languages');
            setLanguages([]);
        } finally {
            setLanguagesLoading(false);
        }
    };

    // Fetch program templates from API
    const fetchProgramTemplates = async () => {
        try {
            setTemplatesLoading(true);
            const response = await axios.get(`${API_URL}/api/program-templates?limit=100`, axiosConfig);
            const result = response.data;
            if (result.success) {
                setProgramTemplates(result.data);
            } else {
                handleError('Failed to fetch program templates');
                setProgramTemplates([]);
            }
        } catch (error) {
            console.error('Error fetching program templates:', error);
            handleError('Failed to fetch program templates');
            setProgramTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    };

    useEffect(() => {
        fetchLanguages();
        fetchProgramTemplates();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle language selection
    const handleLanguageChange = (event, newValue) => {
        setFormData(prev => ({
            ...prev,
            language: newValue ? newValue.name : ''
        }));
    };

    // Handle program template selection
    const handleTemplateChange = (event, newValue) => {
        if (newValue) {
            setSelectedTemplate(newValue);
            setFormData(prev => ({
                ...prev,
                templateId: newValue._id,
                name: newValue.name,
                ageRange: newValue.ageRange
            }));
            setEditedProgramName(newValue.name);
            setEditingProgramName(false);
        } else {
            setSelectedTemplate(null);
            setFormData(prev => ({
                ...prev,
                templateId: '',
                name: '',
                ageRange: []
            }));
            setEditedProgramName('');
            setEditingProgramName(false);
        }
    };

    // Handle program name editing
    const handleEditProgramName = () => {
        setEditingProgramName(true);
    };

    const handleSaveProgramName = () => {
        setFormData(prev => ({
            ...prev,
            name: editedProgramName
        }));
        setEditingProgramName(false);
    };

    const handleCancelProgramName = () => {
        setEditedProgramName(selectedTemplate ? selectedTemplate.name : '');
        setEditingProgramName(false);
    };

    const formatAgeRange = (ageRange) => {
        if (!ageRange || ageRange.length === 0) return null;
        
        const ages = ageRange.map(age => age.age).sort((a, b) => a - b);
        
        if (ages.length === 1) return `${ages[0]} years`;
        if (ages.length === 2) return `${ages[0]}-${ages[1]} years`;
        return `${ages[0]}-${ages[ages.length - 1]} years`;
    };

    const handleVideoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('video/')) {
            handleError('Please select a valid video file');
            return;
        }

        if (file.size > 50 * 1024 * 1024) {
            handleError('Video file size should be less than 50MB');
            return;
        }

        setVideoFile(file);
        setVideoPreview(URL.createObjectURL(file));

        try {
            setUploading(true);
            setUploadProgress(0);

            const uploadFormData = new FormData();
            uploadFormData.append('video', file);

            const response = await axios.post(`${API_URL}/mediaqwerty/upload`, uploadFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                }
            });

            if (response.data.success) {
                const videoUrl = `${API_URL}${response.data.url}`;
                setFormData(prev => ({
                    ...prev,
                    url: videoUrl
                }));
                handleSuccess('Video uploaded successfully');
            } else {
                handleError('Failed to upload video');
            }
        } catch (error) {
            console.error('Upload error:', error);
            if (error.response) {
                const errorMessage = error.response.data?.message || 'Failed to upload video';
                handleError(errorMessage);
            } else if (error.request) {
                handleError('Network error - please check your connection');
            } else {
                handleError('Failed to upload video');
            }
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const removeVideo = () => {
        setVideoFile(null);
        setVideoPreview('');
        setFormData(prev => ({
            ...prev,
            url: ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.language || !formData.programDetail || !formData.name || !selectedTemplate) {
            handleError('Please select a program, language, and fill in the program details');
            return;
        }

        try {
            setLoading(true);
            const submitData = {
                ...formData,
                ageRange: formData.ageRange
            };

            const url = `${API_URL}/api/programs`;
            const response = await axios.post(url, submitData, axiosConfig);
            const result = response.data;

            if (result.success) {
                handleSuccess('Program created successfully');
                setTimeout(() => {
                    navigate('/programs');
                }, 1000);
            } else {
                handleError(result.message || 'Failed to create program');
            }
        } catch (error) {
            console.error('Error creating program:', error);
            if (error.response) {
                const errorMessage = error.response.data?.message || 'Failed to create program';
                handleError(errorMessage);
            } else {
                handleError('Network error - please check your connection');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ 
            marginLeft: '320px',
            marginRight: '24px',
            marginTop: '24px',
            marginBottom: '24px',
            padding: '40px',
            minHeight: '100vh',
            backgroundColor: 'white'
        }}>
            <Container maxWidth="md" sx={{ p: 0 }}>
                {/* Title */}
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography 
                        variant="h4" 
                        component="h1" 
                        fontWeight={700} 
                        color="primary.main"
                        sx={{ mb: 1 }}
                    >
                        Create New Program
                    </Typography>
                   
                </Box>

                <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                        {/* Program Template Selection */}
                        <Box>
                            {templatesLoading ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Loading programs...</Typography>
                                </Box>
                            ) : (
                                <Autocomplete
                                    options={programTemplates}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={selectedTemplate}
                                    onChange={handleTemplateChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Program"
                                            variant="outlined"
                                            required
                                            sx={textFieldStyles}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: <TemplateIcon sx={{ color: '#673AB7', mr: 1 }} />
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box>
                                                <Typography variant="subtitle1">{option.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Ages: {formatAgeRange(option.ageRange)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                    sx={{
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: '#673AB7'
                                        }
                                    }}
                                />
                            )}
                            {!templatesLoading && programTemplates.length === 0 && (
                                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                                    No programs available. Please create programs first in the Program Templates section.
                                </Alert>
                            )}
                        </Box>

                        {/* Selected Template Info */}
                        {selectedTemplate && (
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 3,
                                    border: '2px solid #673AB7',
                                    borderRadius: 3,
                                    backgroundColor: '#f8f9ff'
                                }}
                            >
                                {/* Program Name with Edit Option */}
                                <Box mb={3}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#673AB7', fontWeight: 600 }}>
                                        Program Name:
                                    </Typography>
                                    {editingProgramName ? (
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <TextField
                                                value={editedProgramName}
                                                onChange={(e) => setEditedProgramName(e.target.value)}
                                                size="small"
                                                sx={{
                                                    minWidth: 200,
                                                    backgroundColor: 'white',
                                                    borderRadius: 1,
                                                }}
                                            />
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={handleSaveProgramName}
                                                sx={{ 
                                                    backgroundColor: '#4caf50',
                                                    '&:hover': { backgroundColor: '#45a049' }
                                                }}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={handleCancelProgramName}
                                                sx={{ borderColor: '#673AB7', color: '#673AB7' }}
                                            >
                                                Cancel
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                            <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>
                                                {formData.name}
                                            </Typography>
                                            <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={handleEditProgramName}
                                                sx={{ 
                                                    color: '#673AB7', 
                                                    borderColor: '#673AB7',
                                                    textTransform: 'none',
                                                    fontWeight: 600
                                                }}
                                                variant="outlined"
                                            >
                                                Edit Name
                                            </Button>
                                        </Stack>
                                    )}
                                </Box>

                                {/* Age Range */}
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#673AB7', fontWeight: 600 }}>
                                        Target Age:
                                    </Typography>
                                    <Chip
                                        label={formatAgeRange(selectedTemplate.ageRange)}
                                        sx={{
                                            backgroundColor: '#673AB7',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                            height: 32
                                        }}
                                    />
                                </Box>
                            </Paper>
                        )}

                        {/* Language Dropdown */}
                        <Box>
                            {languagesLoading ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Loading languages...</Typography>
                                </Box>
                            ) : (
                                <Autocomplete
                                    options={languages}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={languages.find(lang => lang.name === formData.language) || null}
                                    onChange={handleLanguageChange}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Language"
                                            variant="outlined"
                                            required
                                            sx={textFieldStyles}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: <LanguageIcon sx={{ color: '#673AB7', mr: 1 }} />
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <LanguageIcon sx={{ mr: 2, color: '#673AB7' }} />
                                            {option.name}
                                        </Box>
                                    )}
                                    sx={{
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: '#673AB7'
                                        }
                                    }}
                                />
                            )}
                            {!languagesLoading && languages.length === 0 && (
                                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                                    No languages available. Please add languages first in the Languages section.
                                </Alert>
                            )}
                        </Box>

                        {/* Program Details */}
                        <TextField
                            label="Program Details"
                            name="programDetail"
                            value={formData.programDetail}
                            onChange={handleChange}
                            required
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            sx={textFieldStyles}
                            placeholder="Describe the program objectives, activities, and learning outcomes..."
                        />

                        {/* Language Template */}
                        <TextField
                            label="Language Template"
                            name="language_temp"
                            value={formData.language_temp}
                            onChange={handleChange}
                            fullWidth
                            variant="outlined"
                            sx={textFieldStyles}
                            placeholder="e.g., pt_BR, es, en"
                        />

                        {/* Template Name */}
                        <TextField
                            label="Template Name"
                            name="tempName"
                            value={formData.tempName}
                            onChange={handleChange}
                            fullWidth
                            variant="outlined"
                            sx={textFieldStyles}
                            placeholder="e.g., bricks_challenge_pt"
                        />

                        {/* Video Upload Section */}
                        <Box>
                            {!formData.url && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        borderRadius: 3,
                                        border: '2px dashed #673AB7',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(103, 58, 183, 0.04)',
                                            borderColor: '#5e35b1',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => document.getElementById('video-upload').click()}
                                >
                                    <input
                                        type="file"
                                        id="video-upload"
                                        accept="video/*"
                                        hidden
                                        onChange={handleVideoUpload}
                                    />
                                    <UploadIcon sx={{ fontSize: 48, color: '#673AB7', mb: 2 }} />
                                    <Typography variant="h6" gutterBottom sx={{ color: '#673AB7', fontWeight: 600 }}>
                                        Click to upload video or drag and drop
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Supported formats: MP4, AVI, MOV (Max 50MB)
                                    </Typography>
                                </Paper>
                            )}

                            {uploading && (
                                <Box mt={2}>
                                    <Typography variant="body2" gutterBottom sx={{ textAlign: 'center' }}>
                                        Uploading video... {uploadProgress}%
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={uploadProgress}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#673AB7',
                                                borderRadius: 4
                                            }
                                        }}
                                    />
                                </Box>
                            )}

                            {formData.url && (
                                <Box sx={{ mt: 2 }}>
                                    {/* Success Message */}
                                    <Box 
                                        display="flex" 
                                        justifyContent="space-between" 
                                        alignItems="center" 
                                        sx={{ 
                                            mb: 2, 
                                            p: 2, 
                                            backgroundColor: '#e8f5e8', 
                                            borderRadius: 2,
                                            border: '1px solid #4caf50'
                                        }}
                                    >
                                        <Typography variant="body1" color="success.dark" fontWeight={600}>
                                            ✅ Video uploaded successfully!
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<DeleteIcon />}
                                            onClick={removeVideo}
                                            sx={{ 
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Box>

                                    {/* Video Preview */}
                                    <Paper 
                                        elevation={1} 
                                        sx={{ 
                                            borderRadius: 2, 
                                            overflow: 'hidden',
                                            backgroundColor: '#000',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {videoPreview && (
                                            <video
                                                src={videoPreview}
                                                controls
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    minHeight: '300px',
                                                    maxHeight: '500px',
                                                    objectFit: 'contain',
                                                    display: 'block'
                                                }}
                                            />
                                        )}
                                    </Paper>
                                </Box>
                            )}
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ pt: 2 }}>
                            <Stack direction="row" spacing={2} justifyContent="center">
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/programs')}
                                    size="large"
                                    sx={{
                                        borderColor: '#673AB7',
                                        color: '#673AB7',
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        minWidth: 120,
                                        '&:hover': {
                                            borderColor: '#5e35b1',
                                            backgroundColor: 'rgba(103, 58, 183, 0.04)'
                                        }
                                    }}
                                >
                                    <CancelIcon sx={{ mr: 1 }} />
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={loading || uploading || !formData.language || !selectedTemplate}
                                    sx={{
                                        backgroundColor: '#673AB7',
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        minWidth: 140,
                                        boxShadow: '0 4px 12px rgba(103, 58, 183, 0.3)',
                                        '&:hover': {
                                            backgroundColor: '#5e35b1',
                                            boxShadow: '0 6px 16px rgba(103, 58, 183, 0.4)'
                                        },
                                        '&:disabled': {
                                            backgroundColor: '#ccc'
                                        }
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <SaveIcon sx={{ mr: 1 }} />
                                            Create Program
                                        </>
                                    )}
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                <ToastContainer />
            </Container>
        </Box>
    );
};

export default ProgramCreate;
